import { AnimatePresence } from "motion/react"
import { useDifficulties } from "~/hooks/useDifficulties"
import { difficultyLabel } from "~/lib/difficulty"
import { issueDifficultyValues } from "~/lib/enums"
import { EntityIdentity, UnassignedIdentity } from "~/components/EntityIdentity"
import { ListSection } from "~/components/SectionHeader"

import { PlusIcon } from "lucide-react"
import { type ComponentProps, type ReactNode, useEffect, useState } from "react"
import { toast } from "sonner"
import posthog from "posthog-js"
import { posthogEnabled } from "~/components/providers/Posthog"
import { Button } from "~/components/ui/button"
import { ScrollArea } from "~/components/ui/scroll-area"
import { IssuePriority, IssueStatus } from "~/lib/enums"
import { api, type RouterOutputs } from "~/trpc/react"
import { DifficultyIcon } from "../icons/difficulty"
import { PriorityIcon } from "../icons/priority"
import { StatusIcon } from "../icons/status"
import { IssueRow } from "./IssueRow"
import { IssueBulkActions } from "./IssueBulkActions"

const statusGroupOrder = [
	IssueStatus.Queue,
	IssueStatus.ToDo,
	IssueStatus.InProgress,
	IssueStatus.InReview,
	IssueStatus.Complete,
	IssueStatus.Blocked,
	IssueStatus.Canceled
]

const statusSortOrder = new Map(
	statusGroupOrder.map((status, index) => [status, index])
)
const prioritySortOrder = new Map(
	[
		IssuePriority.Urgent,
		IssuePriority.High,
		IssuePriority.Medium,
		IssuePriority.Low,
		IssuePriority.None
	].map((priority, index) => [priority, index])
)
const difficultySortOrder = new Map(
	[5, 4, 3, 2, 1, null].map((difficulty, index) => [difficulty, index])
)

function getAssigneeName(
	issue: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]
) {
	return issue.assignee?.orgMember.user?.name ?? ""
}

function sortIssues(
	issues: RouterOutputs["issues"]["getTeamIssues"]["issues"]
) {
	return [...issues].sort((a, b) => {
		const statusDelta =
			(statusSortOrder.get(a.status) ?? Number.MAX_SAFE_INTEGER) -
			(statusSortOrder.get(b.status) ?? Number.MAX_SAFE_INTEGER)
		if (statusDelta) return statusDelta

		const priorityDelta =
			(prioritySortOrder.get(a.priority) ?? Number.MAX_SAFE_INTEGER) -
			(prioritySortOrder.get(b.priority) ?? Number.MAX_SAFE_INTEGER)
		if (priorityDelta) return priorityDelta

		const difficultyDelta =
			(difficultySortOrder.get(a.difficulty) ?? Number.MAX_SAFE_INTEGER) -
			(difficultySortOrder.get(b.difficulty) ?? Number.MAX_SAFE_INTEGER)
		if (difficultyDelta) return difficultyDelta

		const assigneeDelta = getAssigneeName(a).localeCompare(getAssigneeName(b))
		if (assigneeDelta) return assigneeDelta

		return a.title.localeCompare(b.title)
	})
}

function getStatusGroups() {
	return statusGroupOrder.map((status) => ({
		id: status,
		name: status.replace(/([A-Z])/g, " $1").trim(),
		icon: <StatusIcon status={status} />
	}))
}

function getPriorityGroups() {
	return Object.values(IssuePriority).map((priority) => ({
		id: priority,
		name: priority,
		icon: <PriorityIcon priority={priority} />
	}))
}

function getAssigneeGroups(
	issues: RouterOutputs["issues"]["getTeamIssues"]["issues"]
) {
	const assignees = new Map<
		string,
		NonNullable<(typeof issues)[number]["assignee"]>
	>()
	for (const issue of issues) {
		if (issue.assignee?.id && issue.assignee.orgMember.user) {
			assignees.set(issue.assignee.id, issue.assignee)
		}
	}

	const groups: { id: string; name: string; icon: ReactNode }[] = []

	for (const assignee of assignees.values()) {
		if (!assignee.orgMember.user) continue

		groups.push({
			id: assignee.id,
			name: assignee.orgMember.user.name ?? "Unknown",
			icon: (
				<EntityIdentity
					kind="user"
					variant="avatar"
					size="md"
					name={assignee.orgMember.user?.name ?? "?"}
					image={assignee.orgMember.user?.image}
					lastSeenAt={assignee.orgMember.user?.lastSeenAt}
				/>
			)
		})
	}

	groups.push({
		id: "unassigned",
		name: "Unassigned",
		icon: <UnassignedIdentity size="md" />
	})

	return groups
}

function getDifficultyGroups(difficulties: ReturnType<typeof useDifficulties>) {
	return [null, ...issueDifficultyValues].map((difficulty) => ({
		id: String(difficulty),
		name: difficultyLabel(difficulty, difficulties),
		icon: <DifficultyIcon difficulty={difficulty} />
	}))
}

export default function IssueList({
	teamIssueData,
	groupBy = "status",
	onCreateIssue,
	hiddenProperties,
	onIssueOpen
}: {
	teamIssueData: RouterOutputs["issues"]["getTeamIssues"]
	groupBy?: "status" | "priority" | "assignee" | "difficulty"
	onCreateIssue: (value: string) => void
	hiddenProperties?: ComponentProps<typeof IssueRow>["hiddenProperties"]
	onIssueOpen?: (
		issue: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]
	) => void
}) {
	const difficulties = useDifficulties()
	const [selectedIds, setSelectedIds] = useState<string[]>([])
	const selectedIssues = teamIssueData.issues.filter((issue) =>
		selectedIds.includes(issue.id)
	)
	const selectionTeamId = selectedIssues[0]?.teamId
	const selectableIssues = teamIssueData.issues.filter(
		(issue) => issue.teamId === selectionTeamId
	)
	const utils = api.useUtils()
	const bulkUpdate = api.issues.bulkUpdate.useMutation({
		onSuccess: (result, input) => {
			toast.success(
				result.updatedCount
					? `${result.updatedCount} issue${result.updatedCount === 1 ? "" : "s"} updated`
					: "No issues needed updating"
			)
			if (posthogEnabled)
				posthog.capture("issue_bulk_action_used", {
					team_id: input.teamId,
					selected_count: input.issueIds.length,
					updated_count: result.updatedCount,
					properties: Object.keys(input).filter(
						(key) => key !== "teamId" && key !== "issueIds"
					)
				})
		},
		onError: (error) =>
			toast.error(error.message || "Failed to update selected issues"),
		onSettled: () =>
			Promise.all([
				utils.issues.invalidate(),
				utils.favorites.list.invalidate(),
				utils.user.getTeams.invalidate(),
				utils.user.getBadges.invalidate()
			])
	})

	useEffect(() => {
		setSelectedIds((current) => {
			const visible = current.filter((id) =>
				teamIssueData.issues.some((issue) => issue.id === id)
			)
			return visible.length === current.length ? current : visible
		})
	}, [teamIssueData.issues])

	const groups = (() => {
		switch (groupBy) {
			case "status":
				return getStatusGroups()
			case "priority":
				return getPriorityGroups()
			case "assignee":
				return getAssigneeGroups(teamIssueData.issues)
			case "difficulty":
				return getDifficultyGroups(difficulties)
			default:
				return getStatusGroups()
		}
	})()

	const sortedIssues = sortIssues(teamIssueData.issues)
	const issuesByGroup = groups.reduce<
		Record<string, typeof teamIssueData.issues>
	>((acc, group) => {
		acc[group.id] = sortedIssues.filter((issue) => {
			switch (groupBy) {
				case "status":
					return issue.status === group.id
				case "priority":
					return issue.priority === group.id
				case "assignee":
					return group.id === "unassigned"
						? !issue.assignee
						: issue.assignee?.id === group.id
				case "difficulty":
					return String(issue.difficulty) === group.id
				default:
					return false
			}
		})
		return acc
	}, {})

	return (
		<div className="flex h-full min-w-0 flex-col gap-3">
			<ScrollArea
				className="min-h-0 min-w-0 flex-1"
				viewportClassName="[&>div]:block!"
			>
				<div className="flex w-full min-w-0 flex-col gap-5">
					{groups
						.filter((group) => (issuesByGroup[group.id]?.length ?? 0) > 0)
						.map((group) => (
							<ListSection
								key={`${groupBy}-${group.id}`}
								collapsible
								icon={group.icon}
								title={group.name}
								count={issuesByGroup[group.id]?.length ?? 0}
								itemName="issue"
								headerVariant="solid"
								action={
									<Button
										variant="ghost"
										size="icon-xs"
										aria-label={`Create issue in ${group.name}`}
										onClick={() => onCreateIssue(group.id)}
									>
										<PlusIcon />
									</Button>
								}
							>
								<div>
									{issuesByGroup[group.id]?.map((issue) => (
										<IssueRow
											key={issue.id}
											issue={issue}
											hiddenProperties={hiddenProperties}
											onOpen={
												onIssueOpen ? () => onIssueOpen(issue) : undefined
											}
											selected={selectedIds.includes(issue.id)}
											disabled={bulkUpdate.isPending}
											selectionDisabled={
												(!!selectionTeamId &&
													issue.teamId !== selectionTeamId) ||
												(selectedIssues.length >= 100 &&
													!selectedIds.includes(issue.id))
											}
											onSelectionChange={(selected) => {
												if (bulkUpdate.isPending) return
												setSelectedIds((current) => {
													if (!selected)
														return current.filter((id) => id !== issue.id)
													if (
														current.length >= 100 ||
														current.some((id) =>
															teamIssueData.issues.some(
																(candidate) =>
																	candidate.id === id &&
																	candidate.teamId !== issue.teamId
															)
														)
													)
														return current
													return [...new Set([...current, issue.id])]
												})
											}}
										/>
									))}
								</div>
							</ListSection>
						))}
				</div>
			</ScrollArea>
			<AnimatePresence>
				{selectedIssues.length > 0 && (
					<IssueBulkActions
						key="bulk-issue-actions"
						issues={selectedIssues}
						selectableCount={selectableIssues.length}
						pending={bulkUpdate.isPending}
						onClear={() => setSelectedIds([])}
						onSelectAll={() =>
							setSelectedIds(
								[
									...new Set([
										...selectedIssues.map((issue) => issue.id),
										...selectableIssues.map((issue) => issue.id)
									])
								].slice(0, 100)
							)
						}
						onUpdate={(change) => {
							if (
								!selectionTeamId ||
								bulkUpdate.isPending ||
								!selectedIssues.length
							)
								return
							bulkUpdate.mutate({
								...change,
								teamId: selectionTeamId,
								issueIds: selectedIssues.map((issue) => issue.id)
							})
						}}
					/>
				)}
			</AnimatePresence>
		</div>
	)
}
